import {useSessionId} from '../sessionStore/session.hooks';
import {useUser} from './user.hooks';

type MaybeSessionState = ReturnType<typeof useSessionId>['data'];
type MaybeUserState = ReturnType<typeof useUser>['data'];

/**
 *
 * @returns
 * @deprecated use `useIsAuthenticated` instead
 */
export const useIsLoggedIn = (): boolean => {
  const {data: user} = useUser();

  return Boolean(user?.token);
};

export const useUserToken = (): string | null => {
  const {data: user} = useUser();

  return user?.token ?? null;
};

type GenericDataLoadableState<T> =
  | {isLoading: true; data: null}
  | {isLoading: false; data: null}
  | {isLoading: false; data: T};

type TokenOrSessionId =
  | {token: string; resolvedId: string}
  | {sessionId: string; resolvedId: string};

export type MaybeTokenOrSessionId =
  | {token: string; resolvedId: string}
  | {sessionId: string; resolvedId: string}
  | null;

/**
 * This function should be used instead of `useSimpleUserTokenOrSessionId` as it properly waits
 * for either the session (not logged in) or customer (logged in) to be loaded before
 * returning either one. The previous implementation simply fell back to guest if the user
 * was not logged in, which caused double requests to be triggered. This hook also returns the
 * loading state which is useful within pages if we need to wait for this data to be available
 *
 * @returns TokenOrSessionId
 * When returned data is defined, it will always have a `resolvedId` property which is useful in cases where you just need a unique identifier and don't care if it's a token or session id
 */
export const useUserTokenOrSessionId =
  (): GenericDataLoadableState<TokenOrSessionId> => {
    const {data: session, isLoading: isSessionLoading} = useSessionId();
    const {data: user, isLoading: isUserLoading} = useUser();

    if (isUserLoading || isSessionLoading) {
      return {isLoading: true, data: null};
    }

    return {isLoading: false, data: getTokenOrSession(session, user)};
  };

/**
 * @deprecated use `useUserTokenOrSessionId` instead
 */
export const useSimpleUserTokenOrSessionId = (): MaybeTokenOrSessionId => {
  const {data: session} = useSessionId();
  const {data: user} = useUser();

  return getTokenOrSession(session, user);
};

const getTokenOrSession = (
  session: MaybeSessionState,
  user: MaybeUserState,
): MaybeTokenOrSessionId => {
  if (user?.token) {
    return {
      token: user.token,
      resolvedId: user.token,
    };
  }
  if (session?.sessionId) {
    return {
      sessionId: session.sessionId,
      resolvedId: session.sessionId,
    };
  }

  return null;
};
